import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { orderBy } from 'lodash'

import {
  ApiListResponse,
  Asset,
  AssetCategoryAccount,
  PaginatedFetch,
} from '@types'

import { ASSET_CURRENCY_TYPE } from '@/const'

import { prepareResponseError, removeStoreListItem } from '../utils/helpers'

import api from '@/store/api'
import { useRepositoriesStore } from '@/store/repositories/index'

export const useAssetsDeletedStore = defineStore('assets-deleted', () => {
  // INIT
  const repositoriesStore = useRepositoriesStore()

  const list = ref<Asset[]>([])

  const initFlag = ref(false)

  const loading = ref(true)

  let abortController = new AbortController()

  const repositoryId = computed(() => repositoriesStore.getCurrentId)

  // GETTERS

  const getList = computed(() => orderBy(list.value, ['date'], ['desc']))

  const getCommonList = computed(() =>
    getList.value.filter(
      item =>
        ![
          ASSET_CURRENCY_TYPE,
          AssetCategoryAccount.INCOME,
          AssetCategoryAccount.EXPENSE,
        ].includes(item.type),
    ),
  )

  const getCurrencies = computed(() =>
    getList.value.filter(item => item.type === ASSET_CURRENCY_TYPE),
  )

  const getAccounts = computed(() =>
    getList.value.filter(
      item =>
        item.type === AssetCategoryAccount.INCOME ||
        item.type === AssetCategoryAccount.EXPENSE,
    ),
  )

  const getIncomeAccounts = computed(() =>
    getList.value.filter(item => item.type === AssetCategoryAccount.INCOME),
  )

  const getExpenseAccounts = computed(() =>
    getList.value.filter(item => item.type === AssetCategoryAccount.EXPENSE),
  )

  const getCounts = computed(() => ({
    common: getCommonList.value.length,
    currencies: getCurrencies.value.length,
    income: getIncomeAccounts.value.length,
    expense: getExpenseAccounts.value.length,
  }))

  // SETTERS

  const setList = (data: Asset[]) => {
    list.value = data
  }

  const fetch: PaginatedFetch = async (): Promise<void> => {
    if (!repositoryId.value) return
    loading.value = true
    try {
      const result = await api.get<ApiListResponse<Asset[]>>('assets', {
        signal: abortController.signal,
        params: {
          repository_id: repositoryId.value,
          show_deleted: true,
        },
      })
      setList(result.data.data)
      initFlag.value = true
    } catch (e) {
      throw Error(prepareResponseError(e))
    } finally {
      loading.value = false
    }
  }

  const permanentDelete = async (ids: string[]): Promise<void> => {
    try {
      await api.delete('assets?is_hard=1', { data: ids })
      await fetch()
    } catch (e) {
      throw Error(prepareResponseError(e))
    }
  }

  const restore = async (id: string) => {
    try {
      const result = await api.post<Asset>(`assets/${id}/restore`)
      removeStoreListItem(list.value, id)
      return result.data
    } catch (e) {
      throw Error(prepareResponseError(e))
    }
  }

  const bulkRestore = async (ids: string[]): Promise<void> => {
    if (!repositoryId.value || !ids.length) return

    try {
      await api.post(`assets/restore?repository_id=${repositoryId.value}`, ids)
      list.value = list.value.filter(item => !ids.includes(item.id))
    } catch (e) {
      throw Error(prepareResponseError(e))
    }
  }

  const cancel = () => {
    abortController.abort()
    abortController = new AbortController()
  }

  const clear = () => {
    initFlag.value = false
    setList([])
  }

  return {
    initFlag,
    loading,

    getList,
    getCommonList,
    getCurrencies,
    getAccounts,
    getCounts,

    setList,

    fetch,
    permanentDelete,
    restore,
    bulkRestore,

    cancel,
    clear,
  }
})
